import { useEffect } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import {
  InvalidTemplateContents,
  WhatsAppContentArgsInput,
  UpsertTemplateResult,
} from 'modules/graphql/types';
import { isEmpty } from '@iterable/utils';
import { addNegativeToast } from 'modules/toastContainer';
import noOp from 'utils/noop';
import {
  GqlInvalidContentReason,
  GqlUpsertTemplateFailureTypes,
  GqlUpsertTemplateInvalidContentFailureTypes,
} from '../../common/types';
import { getErrorMessage } from '../../common/utils';
import { Config } from './types';

export type UpdateWhatsAppTemplateRequest = {
  templateId?: number;
  campaignId?: number;
  cacheDataFeed: boolean;
  dataFeedIds: number[];
  content: WhatsAppContentArgsInput;
  mergeDataFeedContext: boolean;
  messageTypeId: number;
  name: string;
  referenceDataFeedsByAlias: boolean;
};

type UpdateWhatsAppTemplateResponse = {
  updateWhatsAppTemplate: UpsertTemplateResult;
};

export const UpdateWhatsAppTemplateMutation = gql`
  mutation UpdateWhatsAppTemplate(
    $campaignId: Long
    $templateId: Long!
    $cacheDataFeed: Boolean!
    $name: String!
    $messageTypeId: Long!
    $content: WhatsAppContentArgsInput!
    $dataFeedIds: [Long!]!
    $mergeDataFeedContext: Boolean!
    $referenceDataFeedsByAlias: Boolean!
  ) {
    updateWhatsAppTemplate(
      campaignId: $campaignId
      templateId: $templateId
      cacheDataFeed: $cacheDataFeed
      name: $name
      messageTypeId: $messageTypeId
      content: $content
      dataFeedIds: $dataFeedIds
      mergeDataFeedContext: $mergeDataFeedContext
      referenceDataFeedsByAlias: $referenceDataFeedsByAlias
    ) {
      failure {
        ... on InvalidTemplateContents {
          errors {
            ... on InvalidTemplateContent {
              reasons {
                ... on Unrenderable {
                  fieldName
                  msg
                }
                ... on MissingSnippets {
                  snippets
                }
                ... on InvalidSnippetParams {
                  snippetsWithParams
                }
              }
              content {
                ... on WhatsAppContentArgs {
                  locale {
                    name
                  }
                }
              }
            }
          }
        }
        ... on TemplateNotFound {
          templateId
        }
        ... on InvalidTemplateLocale {
          reason
        }
        __typename
      }
      templateId
    }
  }
`;

export function useUpdateWhatsAppTemplate({
  onComplete = noOp,
  onError = noOp,
  onSuccess = noOp,
}: Config) {
  const dispatch = useDispatch();
  const [upsertTemplate, data] = useMutation<
    UpdateWhatsAppTemplateResponse,
    UpdateWhatsAppTemplateRequest
  >(UpdateWhatsAppTemplateMutation);

  useEffect(() => {
    if (!data.called) {
      return;
    }
    if (!data.loading && data.error) {
      dispatch(addNegativeToast('An unknown error occurred'));
    }

    if (!data.loading && data?.data?.updateWhatsAppTemplate) {
      const { failure, templateId } = data.data.updateWhatsAppTemplate;
      const failures = (Array.isArray(failure) || failure === null ? failure : [failure]) || [];
      const errorMessages: string[] = [];
      const fieldErrors: Record<string, string> = {};
      failures.forEach((err) => {
        if (err.__typename === GqlUpsertTemplateFailureTypes.INVALID_TEMPLATE_CONTENTS) {
          (err as InvalidTemplateContents).errors.forEach((invalidContentErr) =>
            invalidContentErr.reasons.forEach((reason) => {
              const invalidContentReason = reason as GqlInvalidContentReason;
              errorMessages.push(getErrorMessage(invalidContentReason));
              if (
                invalidContentReason.__typename ===
                GqlUpsertTemplateInvalidContentFailureTypes.UNRENDERABLE
              ) {
                fieldErrors[invalidContentReason.fieldName] = invalidContentReason.msg;
              }
            }),
          );
        } else {
          errorMessages.push(getErrorMessage(err));
        }
      });

      errorMessages.forEach((errorMessage) => dispatch(addNegativeToast(errorMessage)));

      if (templateId) {
        onSuccess(templateId);
      } else if (!isEmpty(fieldErrors)) {
        onError(fieldErrors);
      }
    }
    onComplete();
  }, [data.loading]);
  return [upsertTemplate, data] as const;
}
